import React, { memo, useCallback, useState, useMemo, ChangeEvent } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import classNames from 'classnames'
import { Grid, Theme, Box } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import InfiniteScroll from 'react-infinite-scroller'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'

import { Loading, TeamCard } from 'components'
import generalStyles from '../../styles'
import styles from './styles'
import { SearchEmptyResult } from '..'
import { StoreValue } from 'store'
import { ConditionalWrapper, combineStyles } from 'utils'
import TeamsFilterSection from './teamsFilterSection/teamsFilterSection'
import { EAppStateActionTypes } from 'models'
import {
	useFeedTeamsQuery,
	useTeamDepartmentCollectionQuery,
} from 'graphql/__generated__/graphql'

const combinedStyles: any = combineStyles(generalStyles, styles)
const useStyles = makeStyles(combinedStyles)

const Teams: React.FunctionComponent<any> = ({
	showTeams = true,
	storeSearchDebounceQuery,
	storeDispatch,
	storeSearchLoading,
}) => {
	const { t, i18n } = useTranslation()
	const theme: Theme = useTheme()
	const isMdDown: boolean = useMediaQuery(theme.breakpoints.down('md'))

	const classes = useStyles()
	const [isFilterVisible, setIsFilterVisible] = React.useState(false)
	// Teams filters
	const [locationsIds, setLocationsIds] = useState<string[]>([])
	const [industryIds, setIndustryIds] = useState(undefined)
	const [departmentIds, setDepartmentIds] = useState<any>([])
	const [departmentName, setDepartmentName] = useState<string>('')
	const [size, setSize] = useState<string[]>([])
	const [avgAge, setAvgAge] = useState<string[]>([])
	const [companyId, setCompanyId] = useState<string>('')
	const [depFilterKey, setDepFilterKey] = useState<string>('')
	const [filterdDepartmentsCard, setfilterdDepartmentsCard] = useState<{
		[key: string]: boolean
	}>({})
	//todo remaning to add name filter
	const { data: teamsDepartmentsFilteredData } =
		useTeamDepartmentCollectionQuery({
			fetchPolicy: 'network-only',
			// variables: {
			// 	name: storeSearchDebounceQuery,
			// },
		})

	const querySkipCondition =
		!storeSearchDebounceQuery &&
		locationsIds.length === 0 &&
		!industryIds &&
		departmentIds.length === 0 &&
		size.length === 0 &&
		avgAge.length === 0 &&
		!companyId

	const {
		data: feedTeamsData,
		loading: feedTeamsLoading,
		fetchMore: fetchFeedTeams,
	} = useFeedTeamsQuery({
		skip: querySkipCondition,
		fetchPolicy: 'network-only',
		variables: {
			name: Object.keys(storeSearchDebounceQuery).length
				? storeSearchDebounceQuery
				: null,
			industryids: industryIds ? industryIds : null,
			departmentids: !!departmentIds ? departmentIds : null,
			companyid: !!companyId ? companyId : null,
			locationids: locationsIds.length ? locationsIds : null,
			size: size.length ? size : null,
			averageage: avgAge.length ? avgAge : null,
			first: 5,
			after: null,
		},
	})
	const teams = feedTeamsData?.feedteams?.edges
		? feedTeamsData?.feedteams?.edges?.map(edge => edge.node)
		: []
	const hasNextPage = feedTeamsData
		? feedTeamsData && feedTeamsData?.feedteams?.pageInfo?.hasNextPage
		: false

	const handleSetLocationsIds = useCallback(
		(locationsIds: string[]) => {
			setLocationsIds(locationsIds)
		},
		[setLocationsIds],
	)

	const handleSetSize = useCallback((size: string[]) => {
		setSize(size)
	}, [])

	const handleSetAvgAge = useCallback((avgAge: string[]) => {
		setAvgAge(avgAge)
	}, [])

	const handleSetIndustryIds = useCallback(
		ids => {
			if (ids && ids.length > 0) {
				setIndustryIds(ids)
			} else {
				setIndustryIds(undefined)
			}
		},
		[setIndustryIds],
	)
	const handleSetDepartmentIds = useCallback(
		(ids: []) => {
			if (ids && ids.length > 0) {
				let checkDepIds = ids.filter((id: string) =>
					Object.keys(filterdDepartmentsCard).includes(id),
				)
				let updateDepIds = checkDepIds.reduce((acc: any, curr: any) => {
					acc[curr] = true
					return acc
				}, {})
				setfilterdDepartmentsCard({
					...filterdDepartmentsCard,
					...updateDepIds,
				})
				setDepartmentIds(ids)
			} else {
				let updateDepIds = Object.keys(filterdDepartmentsCard).reduce(
					(acc: any, curr: any) => {
						acc[curr] = false
						return acc
					},
					{},
				)
				setfilterdDepartmentsCard({
					...updateDepIds,
				})
				setDepartmentIds([])
			}
		},
		[filterdDepartmentsCard],
	)

	const resetFilteredDepartmentsCard = useCallback(() => {
		let updateDepIds = Object.keys(filterdDepartmentsCard).reduce(
			(acc: any, curr: any) => {
				acc[curr] = false
				return acc
			},
			{},
		)
		setfilterdDepartmentsCard({
			...updateDepIds,
		})
	}, [filterdDepartmentsCard])

	const handleSetCompanyId = useCallback(id => {
		if (id) {
			setCompanyId(id)
		} else {
			setCompanyId('')
		}
	}, [])

	const showFilter = useCallback(() => {
		setIsFilterVisible(true)
	}, [])
	const closeFilter = useCallback(() => {
		setIsFilterVisible(false)
	}, [])

	const toggleDrawer = useCallback(
		(open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event &&
				event.type === 'keydown' &&
				((event as React.KeyboardEvent).key === 'Tab' ||
					(event as React.KeyboardEvent).key === 'Shift')
			) {
				return
			}
			setIsFilterVisible(open)
		},
		[],
	)

	const handleFilterTeamsByDepCard = useCallback(
		(depId: string, title: string) => () => {
			if (depId) {
				// @ts-ignore
				if (!filterdDepartmentsCard[depId]) {
					setfilterdDepartmentsCard({
						...filterdDepartmentsCard,
						// @ts-ignore
						[depId]: true,
					})
					setDepartmentIds([...departmentIds, depId])
				} else {
					const resultTemp = departmentIds.filter(
						(item: string) => item !== depId,
					)
					setfilterdDepartmentsCard({
						...filterdDepartmentsCard,
						// @ts-ignore
						[depId]: false,
					})
					setDepartmentIds(resultTemp)
				}
				setDepartmentName(title)
			}
		},
		[departmentIds, filterdDepartmentsCard],
	)

	useMemo(() => {
		const parsedDepFilteredData =
			teamsDepartmentsFilteredData?.teamCollection?.edges?.map(
				x => x.node,
			) || []

		const parsedDepFilteredIds =
			parsedDepFilteredData && parsedDepFilteredData.length > 0
				? parsedDepFilteredData?.map((item: any) => item.id)
				: []
		const object = parsedDepFilteredIds?.reduce((acc: any, curr: any) => {
			acc[curr] = false
			return acc
		}, {})
		setfilterdDepartmentsCard(object)
	}, [teamsDepartmentsFilteredData])

	const showTeamsDepartments = useCallback(
		(textFilter?: string) => {
			const depsData =
				(teamsDepartmentsFilteredData &&
					teamsDepartmentsFilteredData.teamCollection?.edges?.map(
						x => x.node,
					)) ||
				[]
			const filteredDeps = depsData.filter(item => {
				const depTitle =
					i18n.language === 'ro'
						? item.teamDepartment?.roTitle
						: item.teamDepartment?.roTitle
				const position =
					depTitle?.toLowerCase().search(depFilterKey) ?? 0
				return position >= 0
			})
			return filteredDeps
				? filteredDeps?.map((item, index: number) => {
						const depTitle =
							i18n.language === 'ro'
								? item.teamDepartment?.roTitle
								: item.teamDepartment?.enTitle
						return (
							<Card
								key={`${index}_department`}
								onClick={handleFilterTeamsByDepCard(
									item.id,
									depTitle ?? '',
								)}
								className={classNames(
									classes.card,
									classes.departmentCard,
									{
										// @ts-ignore
										[classes.activeCard]:
											//@ts-ignore
											filterdDepartmentsCard[item.id],
									},
								)}
							>
								<Typography
									className={classes.cardTitle}
									color="textPrimary"
									gutterBottom
								>
									{depTitle}
								</Typography>
								<Typography className={classes.cardSubTitle}>
									{`${teams.length} ${t('teams')}`}
								</Typography>
							</Card>
						)
				  })
				: []
		},
		[
			teamsDepartmentsFilteredData,
			t,
			handleFilterTeamsByDepCard,
			classes,
			filterdDepartmentsCard,
			depFilterKey,
			i18n,
			teams.length,
		],
	)
	const handleDepartmentsFilter = useCallback(
		(ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setDepFilterKey(ev.target.value)
		},
		[],
	)
	const resetFilters = useCallback(() => {
		storeDispatch({
			type: EAppStateActionTypes.updateQuery,
			query: '',
		})
		setLocationsIds([])
		setIndustryIds(undefined)
		setDepartmentIds([])
		setSize([])
		setAvgAge([])
		setCompanyId('')
		resetFilteredDepartmentsCard()
		setDepartmentName('')
	}, [storeDispatch, resetFilteredDepartmentsCard])

	return (
		<>
			<ConditionalWrapper
				condition={showTeams}
				wrapper={(children: any) => (
					<Grid
						className={classes.companiesContainer}
						container={true}
						direction="row"
					>
						{!querySkipCondition && (
							<div className={classes.filterButton}>
								<Hidden smUp>
									<div>
										<Button
											classes={{
												root: classes.filterSectionButton,
											}}
											variant="contained"
											color="primary"
											onClick={toggleDrawer(true)}
										>
											{t('Filter')}
										</Button>
									</div>
								</Hidden>
							</div>
						)}
						{querySkipCondition ? (
							<div className={classNames([classes.pageTitle])}>
								<div>
									{t('Select')}&nbsp;
									<span className={classes.pageTitleMain}>
										{t(`Team Type`)}
									</span>
								</div>
								<div>
									<TextField
										id="departmentFilter"
										label={t('Search')}
										onChange={ev =>
											handleDepartmentsFilter(ev)
										}
										classes={{
											root: classes.teamsFilterDep,
										}}
										variant="outlined"
										size={'small'}
									/>
								</div>
							</div>
						) : (
							<div className={classes.pageTitle}>
								<div>
									{departmentName && (
										<>
											<div
												className={
													classes.backButtonContainer
												}
											>
												<ArrowBackIosIcon
													onClick={resetFilters}
													fontSize={'small'}
													color="primary"
													className={
														classes.titleArrowBack
													}
												/>
											</div>
											<div
												style={{
													display: 'flex',
													flexDirection: 'row',
													alignItems: 'center',
												}}
											>
												<div className={classes.title}>
													{i18n.language === 'en' ? (
														<>
															<div>
																{t('Explore')}
															</div>
															<div>
																{departmentName}
															</div>
															<div id="teams">
																{t('Teams of')}
															</div>
														</>
													) : (
														<>
															<div>
																{t('Explore')}
															</div>
															<div id="teams">
																{t('Teams of')}
															</div>
															<div>
																{departmentName}
															</div>
														</>
													)}
												</div>
											</div>
										</>
									)}
								</div>
								<Hidden only={['xs', 'lg']}>
									<div>
										<Button
											classes={{
												root: classes.filterSectionButton,
											}}
											variant="contained"
											color="primary"
											onClick={toggleDrawer(true)}
										>
											{t('Filter')}
										</Button>
									</div>
								</Hidden>
							</div>
						)}
						<Grid
							className={classes.companiesMainContent}
							container={true}
							direction="column"
						>
							{querySkipCondition && ( // We hide the cards when the search has been made
								<Box className={classes.cardSection}>
									{showTeamsDepartments()}
								</Box>
							)}
							{children}{' '}
						</Grid>
					</Grid>
				)}
			>
				{storeSearchLoading && (
					<Loading size={48} style={{ margin: '50px auto' }} />
				)}
				{!querySkipCondition &&
					!feedTeamsLoading &&
					teams.length === 0 && (
						<SearchEmptyResult query={storeSearchDebounceQuery} />
					)}
				{!feedTeamsLoading && (
					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerCompany,
							classes.fixFlexList,
						)}
					>
						<InfiniteScroll
							pageStart={0}
							loadMore={() =>
								fetchFeedTeams({
									variables: {
										name: Object.keys(
											storeSearchDebounceQuery,
										).length
											? storeSearchDebounceQuery
											: null,
										industryids: industryIds
											? industryIds
											: null,
										departmentids: !!departmentIds
											? departmentIds
											: null,
										companyid: !!companyId
											? companyId
											: null,
										locationids: locationsIds.length
											? locationsIds
											: null,
										size: size.length ? size : null,
										averageage: avgAge.length
											? avgAge
											: null,
										first: 5,
										after: feedTeamsData?.feedteams?.edges[
											feedTeamsData?.feedteams?.edges
												.length - 1
										].cursor,
									},
									updateQuery: (
										previousQueryResult,
										{ fetchMoreResult },
									) => {
										if (
											!fetchMoreResult ||
											!fetchMoreResult.feedteams
										) {
											return previousQueryResult
										}

										const { edges: newEdges, pageInfo } =
											fetchMoreResult.feedteams

										return newEdges.length > 0
											? {
													// Put the new teams at the end of the list and update `pageInfo`
													feedteams: {
														__typename:
															previousQueryResult
																.feedteams
																?.__typename,
														edges: [
															...(previousQueryResult
																.feedteams
																?.edges || []),
															...newEdges,
														],
														pageInfo,
													},
											  }
											: previousQueryResult
									},
								})
							}
							hasMore={hasNextPage}
							loader={
								<div
									style={{ flexGrow: 1, display: 'flex' }}
									key={'loading'}
								>
									<Loading
										size={48}
										style={{ margin: '50px auto' }}
									/>
								</div>
							}
							className={classes.searchContentCards}
						>
							{teams && <TeamCard teamsData={teams} />}
						</InfiniteScroll>
					</div>
				)}
			</ConditionalWrapper>
			{!querySkipCondition && (
				<>
					<Hidden mdDown>
						<div className={classes.filterSection}>
							<TeamsFilterSection
								handleSetIndustryIds={handleSetIndustryIds}
								handleSetLocationsIds={handleSetLocationsIds}
								handleSetSize={handleSetSize}
								handleSetAvgAge={handleSetAvgAge}
								handleSetDepartmentIds={handleSetDepartmentIds}
								departmentIds={departmentIds}
								handleSetCompanyId={handleSetCompanyId}
							/>
						</div>
					</Hidden>
					{isMdDown && (
						<>
							<SwipeableDrawer
								anchor="right"
								open={isFilterVisible}
								onOpen={showFilter}
								onClose={closeFilter}
								classes={{
									paper: classes.filterDrawer,
								}}
							>
								<div className={classes.filterSwipeContainer}>
									<TeamsFilterSection
										handleSetIndustryIds={
											handleSetIndustryIds
										}
										handleSetLocationsIds={
											handleSetLocationsIds
										}
										handleSetSize={handleSetSize}
										handleSetAvgAge={handleSetAvgAge}
										handleSetDepartmentIds={
											handleSetDepartmentIds
										}
										departmentIds={departmentIds}
										handleSetCompanyId={handleSetCompanyId}
										toggleDrawer={toggleDrawer}
									/>
								</div>
							</SwipeableDrawer>
						</>
					)}
				</>
			)}
		</>
	)
}

export default memo((props: any) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'search.debounceQuery|storeSearchDebounceQuery',
			'search.loading|storeSearchLoading',
		]}
	>
		<Teams {...props} />
	</StoreValue>
))
